import { Link } from "react-router-dom";
import { ArrowUpRight, Recycle, Layers, Factory } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import { IMAGES } from "@/lib/content";
import { useContent } from "@/lib/content-context";

const ICONS = [Factory, Recycle, Layers];

export default function Home() {
  usePageTitle("Home");
  const { content } = useContent();
  const h = content.home;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative -mt-24 min-h-[100vh] grain overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${IMAGES.hero}')` }} />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-40 pb-16 min-h-[100vh] flex flex-col justify-between">
          <div className="max-w-4xl">
            <div className="overline text-[#C25934] mb-6 fade-up">{h.hero_overline}</div>
            <h1 data-testid="hero-heading" className="font-serif-display text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight fade-up" style={{ animationDelay: "0.05s" }}>
              {h.hero_line1}<br/>
              <span className="italic font-light">{h.hero_line2}</span>
              <span className="text-[#C25934]">.</span>
            </h1>
            <p className="mt-8 max-w-xl text-[#EAE6DF] text-base md:text-lg leading-relaxed fade-up" style={{ animationDelay: "0.15s" }}>
              {h.hero_subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up" style={{ animationDelay: "0.25s" }}>
              <Link to="/contact" data-testid="hero-inquire-button" className="btn-primary">
                {h.cta_primary_label} <ArrowUpRight size={16} />
              </Link>
              <Link to="/products" data-testid="hero-products-button" className="btn-ghost border-white/30 text-white hover:bg-white hover:text-[#1A1A1A]">
                {h.cta_secondary_label} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/15 mt-16 border border-white/15">
            {(h.stats || []).map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className="bg-black/40 backdrop-blur-sm p-8 md:p-10">
                <div className="font-serif-display text-white text-5xl md:text-6xl leading-none">{s.value}</div>
                <div className="overline mt-4 text-[#C25934]">{s.label}</div>
                <div className="text-sm text-[#D9D4CC] mt-2">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#1A1A1A] text-[#F8F7F4] py-10 overflow-hidden border-y border-[#1A1A1A]">
        <div className="marquee-track font-serif-display text-3xl md:text-5xl italic">
          {[...(content.products.yarn_types || []), ...(content.products.yarn_types || [])].map((y, i) => (
            <span key={i} className="flex items-center gap-24">
              <span>{y.short || y.name}</span>
              <span className="text-[#C25934]">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline mb-5">{h.intro_overline}</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
              {h.intro_heading_line1}<br/>{h.intro_heading_line2}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 text-[#5C5A56] text-lg leading-relaxed">
            <p>{h.intro_paragraph}</p>
            <div className="mt-8">
              <Link to="/about" data-testid="home-about-link" className="link-underline text-[#1A1A1A] font-medium inline-flex items-center gap-2">
                {h.intro_link_text} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS CARDS */}
      <section className="bg-[#EAE6DF] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="overline mb-4">{h.process_overline}</div>
              <h2 className="font-serif-display text-4xl md:text-5xl">{h.process_heading}</h2>
            </div>
            <Link to="/products" data-testid="home-products-cta" className="btn-ghost">
              {h.process_cta} <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {(h.process_cards || []).map((p, i) => {
              const Icon = ICONS[i] || Factory;
              return (
                <div key={i} data-testid={`process-card-${i}`} className="editorial-card p-8 md:p-10">
                  <Icon size={28} className="text-[#2E4A3D]" />
                  <h3 className="font-serif-display text-2xl mt-6">{p.title}</h3>
                  <p className="text-[#5C5A56] mt-3 text-sm leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* RPSY TEASER */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline mb-5">{h.rpsy_overline}</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">{h.rpsy_heading}</h2>
            <p className="mt-6 text-[#5C5A56] text-lg leading-relaxed">{h.rpsy_paragraph}</p>
            <div className="mt-8">
              <Link to="/products" data-testid="home-rpsy-link" className="btn-primary">
                {h.rpsy_button} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="aspect-[4/5] bg-cover bg-center" style={{ backgroundImage: `url('${IMAGES.sustainability}')` }} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2E4A3D] text-[#F8F7F4] py-24 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="overline text-[#D9D4CC] mb-5">{h.cta_overline}</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02]">{h.cta_heading}</h2>
            <p className="mt-4 text-[#D9D4CC] max-w-xl">{h.cta_paragraph}</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/contact" data-testid="home-cta-inquire" className="btn-primary bg-[#C25934] border-[#C25934] hover:bg-[#A84826]">
              {h.cta_button} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
